/**
 * Iter49 · OBS Browser Source Overlays.
 * Public, no-auth, transparent-background. Auto-refresh every 5s.
 * Drop the URL into OBS as a Browser Source and forget about it.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const REFRESH_MS = 5000;

function usePolledData(path, deps = []) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = () => api.get(path).then((r) => !cancelled && setData(r.data)).catch(() => {});
    tick();
    const t = setInterval(tick, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}

function OverlayShell({ children, testid }) {
  // Transparent bg so OBS can composite over any video source.
  return (
    <div className="min-h-screen w-full" style={{ background: "transparent", fontFamily: "Impact, sans-serif" }} data-testid={testid}>
      {children}
    </div>
  );
}

export function OverlayScorebug() {
  const { id } = useParams();
  const d = usePolledData(`/overlays/scorebug/${id}`, [id]);
  if (!d) return <OverlayShell testid="overlay-scorebug" />;
  return (
    <OverlayShell testid="overlay-scorebug">
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-stretch shadow-2xl">
        <div className="px-6 py-3 flex items-center gap-3" style={{ background: d.team_a.color || "#F4D03F", color: "#05050A" }}>
          <span className="text-3xl font-black tracking-tight uppercase">{d.team_a.tag}</span>
          <span className="text-5xl font-black tabular-nums">{d.team_a.score ?? 0}</span>
        </div>
        <div className="px-4 flex items-center bg-black text-white">
          <div className="text-xs uppercase tracking-[0.3em] font-mono">
            {d.status === "live" ? "LIVE" : d.status === "completed" ? "FT" : "VS"}
          </div>
        </div>
        <div className="px-6 py-3 flex items-center gap-3" style={{ background: d.team_b.color || "#B084FF", color: "#05050A" }}>
          <span className="text-5xl font-black tabular-nums">{d.team_b.score ?? 0}</span>
          <span className="text-3xl font-black tracking-tight uppercase">{d.team_b.tag}</span>
        </div>
      </div>
    </OverlayShell>
  );
}

export function OverlayNextMatch() {
  const d = usePolledData(`/overlays/next-match`, []);
  if (!d) return <OverlayShell testid="overlay-next-match" />;
  if (d.empty) {
    return (
      <OverlayShell testid="overlay-next-match">
        <div className="fixed bottom-8 left-8 bg-black/90 text-white p-6 border-l-4 border-[#F4D03F]">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F4D03F]">/ ON DECK</div>
          <div className="text-2xl font-bold mt-2">No fixtures scheduled</div>
        </div>
      </OverlayShell>
    );
  }
  const kickoff = d.scheduled_at ? new Date(d.scheduled_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "TBD";
  return (
    <OverlayShell testid="overlay-next-match">
      <div className="fixed bottom-8 left-8 bg-black/90 text-white p-6 border-l-4 border-[#F4D03F] max-w-md">
        <div className="text-xs uppercase tracking-[0.3em] text-[#F4D03F]">/ ON DECK</div>
        <div className="text-3xl font-black mt-2 tracking-tight uppercase">
          <span style={{ color: d.team_a?.logo_color }}>{d.team_a?.tag || "TBD"}</span>
          <span className="text-gray-500 mx-3">vs</span>
          <span style={{ color: d.team_b?.logo_color }}>{d.team_b?.tag || "TBD"}</span>
        </div>
        <div className="text-sm mt-2 text-gray-300 tracking-widest uppercase">{kickoff}</div>
        {d.map && <div className="text-xs mt-1 font-mono tracking-widest uppercase text-gray-500">Map · {d.map}</div>}
      </div>
    </OverlayShell>
  );
}

export function OverlayPredictionPoll() {
  const { id } = useParams();
  const d = usePolledData(`/overlays/prediction-poll/${id}`, [id]);
  if (!d) return <OverlayShell testid="overlay-prediction-poll" />;
  const total = d.total || 0;
  const aPct = total ? Math.round((d.team_a.votes / total) * 100) : 0;
  const bPct = total ? 100 - aPct : 0;
  return (
    <OverlayShell testid="overlay-prediction-poll">
      <div className="fixed bottom-8 right-8 bg-black/90 text-white p-6 border-r-4 border-[#B084FF] w-96">
        <div className="text-xs uppercase tracking-[0.3em] text-[#B084FF]">/ FAN PREDICTION</div>
        <div className="mt-3 space-y-2">
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-lg font-black uppercase" style={{ color: d.team_a.color }}>{d.team_a.tag}</span>
              <span className="text-2xl font-black tabular-nums">{aPct}%</span>
            </div>
            <div className="h-2 bg-[#232330]"><div className="h-full transition-all duration-500" style={{ width: `${aPct}%`, background: d.team_a.color || "#F4D03F" }} /></div>
          </div>
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-lg font-black uppercase" style={{ color: d.team_b.color }}>{d.team_b.tag}</span>
              <span className="text-2xl font-black tabular-nums">{bPct}%</span>
            </div>
            <div className="h-2 bg-[#232330]"><div className="h-full transition-all duration-500" style={{ width: `${bPct}%`, background: d.team_b.color || "#B084FF" }} /></div>
          </div>
        </div>
        <div className="text-[10px] mt-3 font-mono uppercase tracking-widest text-gray-500">{total} votes · legacyhll.com/predict</div>
      </div>
    </OverlayShell>
  );
}

export function OverlayHero() {
  const { id } = useParams();
  const d = usePolledData(`/overlays/hero/${id}`, [id]);
  if (!d) return <OverlayShell testid="overlay-hero" />;
  return (
    <OverlayShell testid="overlay-hero">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-white/70">{d.division_name || "MATCH DAY"}{d.round ? ` · ROUND ${d.round}` : ""}</div>
          <div className="mt-6 flex items-center gap-8 justify-center">
            <div className="text-right">
              <div className="text-6xl md:text-8xl font-black tracking-tight uppercase" style={{ color: d.team_a.color }}>{d.team_a.tag}</div>
              <div className="text-sm md:text-lg uppercase tracking-widest text-white/70 mt-1">{d.team_a.name}</div>
            </div>
            <div className="text-4xl md:text-6xl font-black text-white/50">vs</div>
            <div className="text-left">
              <div className="text-6xl md:text-8xl font-black tracking-tight uppercase" style={{ color: d.team_b.color }}>{d.team_b.tag}</div>
              <div className="text-sm md:text-lg uppercase tracking-widest text-white/70 mt-1">{d.team_b.name}</div>
            </div>
          </div>
          {d.map && <div className="mt-6 text-lg font-mono uppercase tracking-[0.3em] text-[#F4D03F]">Map · {d.map}</div>}
        </div>
      </div>
    </OverlayShell>
  );
}

export function OverlayRosters() {
  const { id } = useParams();
  const d = usePolledData(`/overlays/rosters/${id}`, [id]);
  if (!d) return <OverlayShell testid="overlay-rosters" />;
  const col = (side) => (
    <div className="flex-1 bg-black/80 text-white p-6">
      <div className="text-2xl font-black uppercase tracking-tight" style={{ color: side.color }}>{side.tag}</div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">{side.roster.length} on roster</div>
      <ul className="mt-4 space-y-1 columns-2 gap-6">
        {side.roster.map((p, i) => (
          <li key={i} className="text-sm font-mono uppercase tracking-widest text-white/90 break-inside-avoid">
            {p.hll_verified ? "✓" : "·"} {p.handle}
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <OverlayShell testid="overlay-rosters">
      <div className="fixed inset-y-0 left-0 right-0 flex items-stretch">
        {col(d.team_a)}
        <div className="w-1 bg-[#F4D03F]" />
        {col(d.team_b)}
      </div>
    </OverlayShell>
  );
}
